import { FC, useMemo, useState } from "react";

import { SectionEnrollmentTable } from "./SectionEnrollmentTable";

import { serverGetSectionEnrollmentSummary } from "api/api-server-data";
import { CourseMenu } from "components/menus/CourseMenu";
import { SectionMenu } from "components/menus/SectionMenu";
import { TermMenu } from "components/menus/TermMenu";
import { t } from "i18n/i18n";
import { IconText } from "mds/components/IconText";
import { SquareButton } from "mds/components/buttons/SquareButton";
import { ArrowDownToLineIcon } from "mds/icons";
import { useAppSelector } from "store/index";
import { termSelectors } from "store/selectors";
import { usePromiseEffect } from "store/store-hooks";
import { toastErrorMessage } from "utils/alerts";
import { downloadCSVFile } from "utils/file";
import { useCourseIdsInTerm } from "utils/hooks/use-course-ids-in-term";
import { generateSectionEnrollmentCSV } from "utils/reporting";

/** Max rows to render in the table; full data still used for CSV download. */
const DISPLAY_ROW_CAP = 300;

export const SectionEnrollmentReport: FC = () => {
  const [selectedCourseId, setSelectedCourseId] = useState("");
  const [selectedTermId, setSelectedTermId] = useState("");
  const [selectedSectionId, setSelectedSectionId] = useState("");
  const terms = useAppSelector(termSelectors.selectAll);
  const getCourseIdsInTerm = useCourseIdsInTerm();
  const courseIdsInTerm = useMemo(
    () => getCourseIdsInTerm(selectedTermId),
    [getCourseIdsInTerm, selectedTermId],
  );

  const handleTermChange = (termId: string) => {
    setSelectedTermId(termId);
    setSelectedSectionId("");
    if (!getCourseIdsInTerm(termId).has(selectedCourseId)) setSelectedCourseId("");
  };

  const handleCourseChange = (courseId: string) => {
    setSelectedCourseId(courseId);
    setSelectedSectionId("");
  };

  const { data, isLoading, error } = usePromiseEffect(async () => {
    if (!selectedTermId) {
      return [];
    }

    return serverGetSectionEnrollmentSummary({
      term_id: selectedTermId,
      course_id: selectedCourseId || undefined,
      section_id: selectedSectionId || undefined,
    });
  }, [selectedTermId, selectedCourseId, selectedSectionId]);

  const handleDownloadReport = () => {
    if (!selectedTermId) {
      toastErrorMessage(t("section_enrollment_report.select_term_to_view_data"));
      return;
    }

    if (!data || data.length === 0) {
      toastErrorMessage(t("section_enrollment_report.no_data"));
      return;
    }

    try {
      const selectedTerm = terms.find((term) => term.id === selectedTermId);
      const termTitle = selectedTerm?.title || selectedTermId;

      const { csvData, filename } = generateSectionEnrollmentCSV(data, termTitle);
      downloadCSVFile(csvData, filename);
    } catch {
      toastErrorMessage(t("section_enrollment_report.export_failed"));
    }
  };

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <h2 className="h3">{t("section_enrollment_report.title")}</h2>
      </div>

      {/* Filters */}
      <div className="mb-4 flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2">
          <TermMenu
            defaultToAll={false}
            eventPrefix="Home - Reports / "
            menuType="menu"
            selectedTermId={selectedTermId}
            size="s"
            onTermChange={handleTermChange}
          />
        </div>

        <div className="flex items-center gap-2">
          <CourseMenu
            disabled={!selectedTermId}
            filterBy={(c) => courseIdsInTerm.has(c.id)}
            menuType="menu"
            selectedCourseId={selectedCourseId}
            size="s"
            adminAccessibleOnly
            onCourseChange={handleCourseChange}
          />
        </div>

        <div className="flex items-center gap-2">
          <SectionMenu
            disabled={!selectedTermId}
            emptyOptionText={t("import_modal.all_sections")}
            filterBy={(s) =>
              s.term_id === selectedTermId &&
              (!selectedCourseId || s.course_id === selectedCourseId)
            }
            menuType="menu"
            selectedSectionId={selectedSectionId}
            size="s"
            onSectionChange={setSelectedSectionId}
          />
        </div>

        <div className="flex items-center gap-2">
          <SquareButton
            disabled={!selectedTermId || !data || data.length === 0}
            eventName="Home - Reports / Download section enrollment"
            kind="tertiary"
            size="s"
            disableWhilePending
            onClick={handleDownloadReport}
          >
            <IconText
              iconStart={<ArrowDownToLineIcon />}
              text={t("section_enrollment_report.download")}
            />
          </SquareButton>
        </div>
      </div>

      {/* Error message */}
      {error && <div className="bg-red-50 text-red-700 mb-4 rounded-md p-4">{error.message}</div>}

      {/* Loading state */}
      {isLoading && (
        <div className="mt-8 text-center text-black-tint-40">{t("common.loading")}...</div>
      )}

      {/* Table: render only first N rows to keep UI responsive; full data used for CSV */}
      {data && (
        <>
          {data.length > DISPLAY_ROW_CAP && (
            <p className="mb-2 text-sm text-black-tint-40">
              {t("section_enrollment_report.showing_first_of", {
                count: DISPLAY_ROW_CAP,
                total: data.length,
              })}
            </p>
          )}

          <SectionEnrollmentTable data={data.slice(0, DISPLAY_ROW_CAP)} />
        </>
      )}

      {/* No data message */}
      {data && !error && data.length === 0 && (
        <div className="mt-4 text-black-tint-40">
          {selectedTermId
            ? t("section_enrollment_report.no_data")
            : t("section_enrollment_report.select_term_to_view_data")}
        </div>
      )}
    </div>
  );
};
